/**
 * Deterministic id generation for widget data.
 *
 * These helpers are platform-neutral (no DOM/React) so both the web client and
 * a future React Native UI can back-fill stable ids for widget items that arrive
 * without one. Kept in the domain package alongside the widget schemas so the
 * shared widget spec map (`widget-spec.ts`) can consume them.
 */
import type { FlightOptionIn } from './flight-schemas.js';
import type { MapPointIn, WidgetImageIn } from './widget-schemas.js';

import { makeId } from '@elara/core/utils';

export function slugify(str: string, { maxLength }: { maxLength?: number } = {}): string {
  let slug = str
    .toLowerCase()
    .replaceAll(' ', '-')
    .replaceAll(/[^a-z0-9-]/g, '-')
    .replaceAll(/-+/g, '-');
  if (maxLength && slug.length > maxLength) {
    slug = slug.slice(0, maxLength);
  }
  return slug.replaceAll(/^-|-$/g, '');
}

function slugifyAny(
  description: string | Array<string | undefined | null | false>,
  { idx, maxLength = 100 }: { idx?: number; maxLength?: number } = {},
): string {
  let str = Array.isArray(description) ? description.find((d) => !!d && d.trim().length > 0) : description?.trim();
  if (!str) {
    console.warn('No string to slugify from description, falling back to random id', description);
    str ||= makeId({ length: 10 });
  }

  const suffix = idx !== undefined ? `-${idx}` : '';
  const effectiveMaxLength = maxLength ? maxLength - suffix.length : undefined;

  return slugify(str, { maxLength: effectiveMaxLength }) + suffix;
}

export function generatePointId(point: MapPointIn, idx?: number): string {
  const coords =
    point.latitude !== undefined && point.longitude !== undefined ? `${point.latitude} ${point.longitude}` : undefined;
  return slugifyAny([point.label, point.address, point.description, coords], { idx });
}

export function generateFlightOptionId(flightOption: FlightOptionIn, idx?: number): string {
  const segments = flightOption.segments ?? [
    ...(flightOption.outboundSegments ?? []),
    ...(flightOption.returnSegments ?? []),
  ];
  return slugifyAny(segments.map((segment) => segment.origin.airport_name).join(','), {
    idx,
    maxLength: 100,
  });
}

export function generateImageId(image: WidgetImageIn, idx: number): string {
  return slugifyAny([image.caption, image.attribution, image.alt, image.url], { idx });
}
